import { TransferList } from "@components/TransferList";
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
// import { DataGrid } from "@mui/x-data-grid";
import { GridColDef } from "@mui/x-data-grid-pro";
import {
  ModelPerson,
  ModelRole,
  TeamTrainingProgramCreateUserInput,
  TeamTrainingProgramUserMetadata
} from "@sportsgravyengineering/sg-api-react-sdk";
import { useEffect, useMemo } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { UserOverride } from "./UserOverride";
import { RenderTableView } from "@components/RenderTableView";
import { ActiveUserIcon, PendingUserIcon } from "@components/Icons";
import { Delete } from "@mui/icons-material";
import { ToolTip } from "@components/ToolTip";

export interface ManagerOption extends TeamTrainingProgramCreateUserInput {
  label: string;
  id: string;
  value: string;
  parent?: ManagerOption;
  status?: string;
}

interface ManagerSelectionFormProps {
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<FieldValues, any, undefined>;
  setManagers: (value: string[]) => void;
  isEditing?: boolean;
  isLoadingUsers?: boolean;
  userOptions: ModelPerson[];
  subRoleOptions: ModelRole[];
  managers: string[];
  managersWithValues: ManagerOption[];
  setManagersWithValues: (value: ManagerOption[]) => void;
  roleId: string;
  setUserOptions?: (value: ModelPerson[]) => void;
}

export const ManagerSelectionForm = (props: ManagerSelectionFormProps) => {
  const getUpdatedUserOptions = (
    userOptions: ModelPerson[],
    usersWithValues
  ) => {
    const includedUsers = usersWithValues.filter(
      (u) =>
        u.user &&
        u.user.person &&
        userOptions.findIndex((option) => option.personId === u.id) === -1
    );
    if (includedUsers.length) {
      props.setUserOptions!([
        ...userOptions,
        ...includedUsers.map((u) => {
          return u.user.person;
        })
      ]);
      return [
        ...userOptions,
        ...includedUsers.map((u) => {
          return u.user.person;
        })
      ];
    } else return userOptions;
  };

  const managerOptions = useMemo(() => {
    if (!props.disabled && props.userOptions) {
      const updatedUserOptions = getUpdatedUserOptions(
        props.userOptions,
        props.managersWithValues
      );
      return updatedUserOptions.map((p) => ({
        id: p.personId!,
        value: p.personId!,
        label: `${p.firstName} ${p.lastName}`,
        ...p
      }));
    } else return [];
  }, [props.userOptions]);

  useEffect(() => {
    if (!props.disabled) {
      const prevManagerIds = props.managersWithValues.map(
        (manager) => manager.personId
      );
      const newManagerIds = props.managers.filter(
        (managerId) => !prevManagerIds.includes(managerId)
      );
      if (props.managers.length < props.managersWithValues.length) {
        props.setManagersWithValues([
          ...props.managersWithValues.filter((managerWithValue) =>
            props.managers.includes(managerWithValue.id)
          )
        ]);
      } else {
        props.setManagersWithValues([
          ...props.managersWithValues,
          ...newManagerIds.map((id) => {
            const user =
              props.userOptions.find((u) => u.personId === id) ||
              managerOptions.find((u) => u.personId === id);
            return {
              id: user!.personId!,
              value: user!.personId!,
              personId: user!.personId!,
              label: `${user?.firstName} ${user?.lastName}`,
              ...user,
              roleId: props.roleId,
              permissions: []
            };
          })
        ]);
      }
    }
  }, [props.managers, props.userOptions]);

  const onDelete = (row) => {
    const index = props.managers.indexOf(row.id);
    if (index !== -1) {
      props.setManagers([
        ...props.managers.slice(0, index),
        ...props.managers.slice(index + 1)
      ]);
      props.setManagersWithValues([
        ...props.managersWithValues.slice(0, index),
        ...props.managersWithValues.slice(index + 1)
      ]);
    }
  };

  const LIST_COLUMNS: GridColDef[] = [
    {
      field: "action",
      headerName: "",
      width: 50,
      renderCell: (params) => (
        <IconButton onClick={() => onDelete(params.row)}>
          <Delete />
        </IconButton>
      )
    },
    {
      headerName: "Name",
      field: "label",
      minWidth: 250,
      flex: 1,
      sortable: false
    },
    {
      headerName: "Role",
      field: "role",
      width: 120,
      flex: 1,
      renderCell: () => {
        return <div>Manager</div>;
      },
      sortable: false
    },
    ...(props.subRoleOptions?.length > 0
      ? [
          {
            headerName: "Sub Role",
            field: "managerSubRole",
            minWidth: 350,
            flex: 1,
            renderCell: (params) => {
              const role = props?.subRoleOptions?.find(
                (sr) =>
                  sr.roleId === params.row.roleId ||
                  sr.inheritedFromId === params.row.roleId
              )?.roleId;
              return (
                <Select
                  value={role}
                  onChange={(event) => {
                    //update the roleId in the managersWithValues
                    props.setManagersWithValues(
                      props.managersWithValues.map((item) => {
                        if (item.personId === params.row.personId) {
                          return {
                            ...item,
                            roleId: event.target.value
                          };
                        }
                        return item;
                      })
                    );
                  }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                  style={{ width: "70%", height: 40 }}
                >
                  {props?.subRoleOptions?.map((item) => (
                    <MenuItem key={item.roleId} value={item.roleId}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              );
            },
            sortable: false
          }
        ]
      : []),
    {
      headerName: "Title",
      field: "managerTitle",
      minWidth: 250,
      flex: 1,
      renderCell: (params) => (
        <Box>
          <TextField
            placeholder=""
            type={"text"}
            size="small"
            style={{ width: "100%" }}
            value={
              params.row.metadata
                ? (params.row.metadata as TeamTrainingProgramUserMetadata)[
                    "title"
                  ]
                : ""
            }
            onKeyDown={(event) => {
              if (event.keyCode === 32) {
                event.stopPropagation();
              }
            }}
            onChange={(event) => {
              //update the roleId in the managersWithValues
              props.setManagersWithValues(
                props.managersWithValues.map((item) => {
                  if (item.personId === params.row.personId) {
                    return {
                      ...item,
                      metadata: {
                        title: event.target.value
                      }
                    };
                  }
                  return item;
                })
              );
            }}
          />
        </Box>
      ),
      sortable: false
    }
  ];

  const LIST_COLUMNS_VIEW: GridColDef[] = [
    {
      headerName: "Name",
      field: "label",
      minWidth: 250,
      flex: 1,
      sortable: false
    },
    {
      headerName: "Role",
      field: "role",
      width: 120,
      flex: 1,
      sortable: false,
      renderCell: () => {
        return <div>Manager</div>;
      }
    },
    ...(props.subRoleOptions?.length > 0
      ? [
          {
            headerName: "Sub Role",
            field: "managerSubRole",
            minWidth: 200,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
              let index = props?.subRoleOptions.findIndex(
                (subRole) => subRole.roleId === params.row.roleId
              );
              if (index === -1) {
                index = props?.subRoleOptions?.findIndex(
                  (sr) => sr.inheritedFromId === params.row.roleId
                );
              }
              return (
                <>
                  {index !== -1 ? props?.subRoleOptions?.at(index)?.name : ""}
                </>
              );
            }
          }
        ]
      : []),
    {
      headerName: "Title",
      field: "managerTitle",
      minWidth: 200,
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <>
          {params.row.metadata && params.row.metadata.title
            ? params.row.metadata.title
            : ""}
        </>
      )
    },
    {
      headerName: "Status",
      field: "status",
      minWidth: 100,
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        if (params.row.status === "ACTIVE") {
          return (
            <>
              <ToolTip title="User Accepted" placement="right">
                <div {...props} data-testid="MANAGER_USER_ACCEPTED">
                  <ActiveUserIcon />
                </div>
              </ToolTip>
            </>
          );
        } else if (params.row.status === "PENDING")
          return (
            <>
              <ToolTip title="Invite Pending" placement="right">
                <div {...props} data-testid="MANAGER_USER_PENDING">
                  <PendingUserIcon />
                </div>
              </ToolTip>
            </>
          );
        else return <></>;
      }
    }
  ];
  return (
    <Grid container direction="column" spacing="25px">
      <Grid item container direction="row" spacing="24px">
        {!props.disabled && (
          <Grid item xs={12}>
            <TransferList
              name="managers"
              label="Managers"
              control={props.form.control}
              isLoading={props.isLoadingUsers}
              options={managerOptions}
              required={false}
              setValue={props.form.setValue}
              setState={props.setManagers}
              state={props.managers}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <RenderTableView
            title="Coaches"
            hideToolbar
            hasActionColumn={false}
            rows={props.managersWithValues}
            columns={props.disabled ? LIST_COLUMNS_VIEW : LIST_COLUMNS}
            hideFilter
            getRowId={(row) => row?.id}
            searchable={false}
          />
        </Grid>
        {!props.disabled && (
          <Grid item xs={12}>
            <UserOverride
              title="Manager"
              roleId={props.roleId}
              userOptions={props.userOptions}
              setUserOptions={props.setUserOptions!}
              masterList={props.managers}
              setMasterList={props.setManagers}
              roleAlias="MANAGER"
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
